import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Button, Flex, Input, InputNumber, Tooltip } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Well } from '@pushly/aqe/lib/components'
import { getRandId } from '@pushly/models/lib/utils/get-rand-id'
import { JourneyNode, NodeEditorRef, NodeEditorSubmitResult } from '../../types/journey-nodes'
import type { IVariantSplitStep } from '@pushly/models/lib/structs/journeys'
import { updateStepConfig } from '@pushly/models/lib/structs/journeys'
import { JourneyBuilderMode } from '../../enums/journey-builder-mode.enum'
import { useJourneyContext } from '../../context'
import { JourneyAction } from '../../types/journey-context'
import { validateSplitStepFields, BranchFieldError, SummaryError } from '../../utils/graph/validation'
import { autoDistribute } from '../../utils/graph/traversal'
import type { IVariantBranch } from '@pushly/models/lib/structs/journeys'
import './split-step-editor.scss'

const VARIANT_LETTERS = ['A', 'B', 'C', 'D']
const MAX_VARIANTS = 4
const MIN_VARIANTS = 2

type SplitStepEditorProps = {
    node: JourneyNode<IVariantSplitStep>
}

export const SplitStepEditor = forwardRef<NodeEditorRef, SplitStepEditorProps>(({ node }, ref) => {
    const [state, dispatch] = useJourneyContext()
    const [branches, setBranches] = useState<IVariantBranch[]>(() =>
        node.data.step.configuration.branches.map((v) => ({ ...v }) as IVariantBranch),
    )

    const isReadonly = state.mode === JourneyBuilderMode.READONLY

    // Run validation on every change
    const validation = useMemo(() => validateSplitStepFields(branches), [branches])

    const hasErrors = !validation.valid

    // Build lookup maps for field errors
    const fieldErrors = useMemo(() => {
        const nameErrors = new Map<string, string>()
        const pctErrors = new Map<string, string>()
        let sumError: string | undefined

        for (const err of validation.errors) {
            if ('branchKey' in err) {
                const fe = err as BranchFieldError
                if (fe.field === 'name') nameErrors.set(fe.branchKey, fe.message)
                else if (fe.field === 'percentage') pctErrors.set(fe.branchKey, fe.message)
            } else {
                const se = err as SummaryError
                if (se.field === 'percentageSum') sumError = se.message
            }
        }

        return { nameErrors, pctErrors, sumError }
    }, [validation])

    const percentageSum = useMemo(() => branches.reduce((sum, v) => sum + v.percentage, 0), [branches])

    // Disable submit button when errors exist
    useEffect(() => {
        if (isReadonly) return

        dispatch({
            action: JourneyAction.SET_EDITOR_FOOTER,
            data: (
                <Button
                    type="primary"
                    disabled={hasErrors}
                    onClick={async () => {
                        const result = await (ref as React.MutableRefObject<NodeEditorRef>)?.current?.onSubmit()
                        if (result) {
                            dispatch({
                                action: JourneyAction.ON_NODE_STEP_CHANGE,
                                data: result as { nodeId: string; step: any },
                            })
                            dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: null })
                        }
                    }}
                >
                    Update Step
                </Button>
            ),
        })
    }, [hasErrors, isReadonly, dispatch])

    // Cleanup footer on unmount
    useEffect(() => {
        return () => {
            dispatch({ action: JourneyAction.SET_EDITOR_FOOTER, data: undefined })
        }
    }, [dispatch])

    useImperativeHandle(ref, () => ({
        onSubmit: (): NodeEditorSubmitResult => {
            return {
                nodeId: node.id,
                step: updateStepConfig(node.data.step, { branches }),
            }
        },
    }))

    const getNextVariantName = useCallback((): string => {
        const usedNames = new Set(branches.map((v) => v.label))
        for (const letter of VARIANT_LETTERS) {
            const name = `Variant ${letter}`
            if (!usedNames.has(name)) return name
        }
        return `Variant ${VARIANT_LETTERS[branches.length] ?? String(branches.length + 1)}`
    }, [branches])

    const handleAddVariant = useCallback(() => {
        if (branches.length >= MAX_VARIANTS) return

        const newCount = branches.length + 1
        const percentages = autoDistribute(newCount)
        const newBranch: IVariantBranch = {
            kind: 'variant',
            key: getRandId(),
            label: getNextVariantName(),
            percentage: percentages[newCount - 1],
            out_step_id: node.data.step.configuration.join_step_id,
        }

        const updated = branches.map((v, i) => ({ ...v, percentage: percentages[i] }))
        updated.push(newBranch)
        setBranches(updated)
    }, [branches, getNextVariantName, node])

    const handleRemoveVariant = useCallback(
        (branch: IVariantBranch) => {
            if (branches.length <= MIN_VARIANTS) return

            const remaining = branches.filter((v) => v.key !== branch.key)
            const percentages = autoDistribute(remaining.length)
            const updated = remaining.map((v, i) => ({ ...v, percentage: percentages[i] }))

            setBranches(updated)
        },
        [branches],
    )

    const handleNameChange = useCallback((branchKey: string, label: string) => {
        setBranches((prev) => prev.map((v) => (v.key === branchKey ? { ...v, label } : v)))
    }, [])

    const handlePercentageChange = useCallback((branchKey: string, value: number | null) => {
        const percentage = value ?? 0
        setBranches((prev) => prev.map((v) => (v.key === branchKey ? { ...v, percentage } : v)))
    }, [])

    return (
        <Well className="split-step-editor" mode="ghost" title="Variants" footerMode="hidden" padding="none">
            <Flex vertical gap={8}>
                {branches.map((branch) => (
                    <Flex gap={8} key={branch.key} className="variant-row" align="flex-start">
                        <div className="variant-name-field">
                            <Input
                                placeholder="Variant name"
                                aria-label={`${branch.label || 'Variant'} name`}
                                aria-invalid={fieldErrors.nameErrors.has(branch.key) || undefined}
                                aria-describedby={
                                    fieldErrors.nameErrors.has(branch.key)
                                        ? `name-err-${branch.key}`
                                        : undefined
                                }
                                value={branch.label}
                                maxLength={50}
                                disabled={isReadonly}
                                status={fieldErrors.nameErrors.has(branch.key) ? 'error' : undefined}
                                onChange={(e) => handleNameChange(branch.key, e.target.value)}
                            />
                            {fieldErrors.nameErrors.has(branch.key) && (
                                <div className="field-error" id={`name-err-${branch.key}`}>
                                    {fieldErrors.nameErrors.get(branch.key)}
                                </div>
                            )}
                        </div>

                        <div className="variant-percentage-field">
                            <InputNumber
                                min={1}
                                max={99}
                                precision={0}
                                aria-label={`${branch.label || 'Variant'} percentage`}
                                aria-invalid={fieldErrors.pctErrors.has(branch.key) || undefined}
                                aria-describedby={
                                    fieldErrors.pctErrors.has(branch.key)
                                        ? `pct-err-${branch.key}`
                                        : undefined
                                }
                                value={branch.percentage}
                                disabled={isReadonly}
                                status={fieldErrors.pctErrors.has(branch.key) ? 'error' : undefined}
                                addonAfter="%"
                                onChange={(value) => handlePercentageChange(branch.key, value)}
                            />
                            {fieldErrors.pctErrors.has(branch.key) && (
                                <div className="field-error" id={`pct-err-${branch.key}`}>
                                    {fieldErrors.pctErrors.get(branch.key)}
                                </div>
                            )}
                        </div>

                        {/* Variants are removed via promotion (in the Split Test Results
                            drawer), not individual deletion here; the trash is locked with a
                            tooltip steering users there. The MIN_VARIANTS guard is preserved;
                            the tooltip only shows when the button is disabled by that rule. */}
                        <Tooltip
                            title={
                                !isReadonly && branches.length <= MIN_VARIANTS
                                    ? "Variants can't be deleted individually. Open Split Test Results to promote a winner and end the test."
                                    : undefined
                            }
                            placement="top"
                        >
                            {/* Wrap in a span: antd disables the Button with pointer-events:none,
                                so without the span the Tooltip never receives hover and the
                                steering message can't surface (the case it exists for). */}
                            <span
                                style={{
                                    display: 'inline-block',
                                    cursor:
                                        isReadonly || branches.length <= MIN_VARIANTS ? 'not-allowed' : undefined,
                                }}
                            >
                                <Button
                                    className="variant-remove-btn"
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    disabled={isReadonly || branches.length <= MIN_VARIANTS}
                                    onClick={() => handleRemoveVariant(branch)}
                                />
                            </span>
                        </Tooltip>
                    </Flex>
                ))}

                <Button
                    className="add-variant-btn"
                    type="dashed"
                    icon={<PlusOutlined />}
                    disabled={isReadonly || branches.length >= MAX_VARIANTS}
                    onClick={handleAddVariant}
                    block
                >
                    Add Variant
                </Button>
                <div className={`percentage-sum-indicator ${percentageSum === 100 ? 'valid' : 'error'}`}>
                    Total: {percentageSum}% / 100%
                </div>
                {fieldErrors.sumError && (
                    <div className="field-error" role="alert">
                        {fieldErrors.sumError}
                    </div>
                )}
            </Flex>
        </Well>
    )
})
